import { Options, Splide } from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import GLightbox from "glightbox";
import { PageListingOptions } from "../../Models/page-listing-option";
import { RenderTagList } from "./search/search-helpers";
import { CarouselModel } from "./search/search-model";

export default class DapthCarousel {
    parentElem: HTMLElement;
    containerElem: HTMLElement;

    closeBtn: HTMLButtonElement;

    mainCarousel: Splide;
    mainCarouselElem: HTMLElement;

    thumbnails: HTMLElement[];
    lightboxThumbnails: HTMLElement[] = [];

    lightbox: any;
    lightboxElem: HTMLElement;
    lightboxInitialized = false; 

    options: PageListingOptions;
    isPaginationUpdated: boolean = false;
    carouselModel: CarouselModel;
    constructor (elemId: string, carouselOptions: string) {
        this.parentElem = document.getElementById(elemId);
        this.options = JSON.parse(carouselOptions);
        this.carouselModel = { itemList: Array.from(this.parentElem.querySelectorAll('.splide__slide') as NodeListOf<HTMLElement>) }

        if (this.parentElem) {
            this.initElements();
        }
        this.initialiseCarousel();

        if (this.options.enableLightbox && window.innerWidth > 992) {
            this.initLightbox();
            this.lightboxInitialized = true;
        }
        else if (window.innerWidth < 992) {
            this.updateTabIndexOnCarousel(false);
        }

        window.addEventListener('resize', () => {
            if (window.innerWidth < 992) {
                this.updateTabIndexOnCarousel(false);
                if (this.lightbox) {
                    this.lightbox.close();
                    this.lightboxInitialized = false;
                    this.lightbox.destroy();
                    this.lightbox = null;

                }
            } else if (this.options.enableLightbox && !this.lightboxInitialized) {
                this.initLightbox();
                this.lightboxInitialized = true;
            }
        });
    }

    private initElements() {
        this.containerElem = this.parentElem.querySelector('.dapth-carousel');
        this.mainCarouselElem = this.parentElem.querySelector<HTMLElement>(".splide");
        this.thumbnails = Array.from(this.parentElem.getElementsByClassName('thumbnail') as HTMLCollectionOf<HTMLElement>);
    }

    private initialiseCarousel(): void {
        let current: HTMLElement = null;
        let extensions = {};
        let conditionOptions = {};

        if (this.options.autoScroll) {
            conditionOptions = {
                AutoScroll: {
                    speed: Number(this.options.autoScrollSpeed)
                }
            }
            extensions = { AutoScroll };
        } 

        const carouselOptions: Options = {
            type: this.options.autoScroll ? 'loop' : 'fade',
            autoplay: this.options.autoplay,
            interval: this.options.autoplayInterval,
            pauseOnHover: this.options.pauseOnHover,
            perPage: 1,
            perMove: 1,
            rewind: true,
            arrows: this.options.displayArrows,
            pagination: this.options.displayPagination,
            drag: this.options.autoScroll && this.options.draggable ? 'free' : this.options.draggable,
            lazyLoad: 'nearby',
            maxWidth: '900px',
            gap: '1.5rem',
            focus: 'center',
            mediaQuery: 'min',
            breakpoints: {
                991: {
                    width: 750,
                },
            },
            ...conditionOptions
        };

        this.mainCarousel = new Splide(this.mainCarouselElem, carouselOptions);
        this.mainCarousel.on('active', (slide) => {
            (this.options.displayPagination && !this.isPaginationUpdated) && this.updatePageNumber(slide.slideIndex);
        });

        
        if (this.options.displayThumbnail) {
            for (var i = 0; i < this.thumbnails.length; i++) {
                this.initThumbnail(this.thumbnails[i], i);
            }
            this.mainCarousel.on('mounted move', () => {
                if (current) {
                    current.classList.remove('is-active');
                    current.toggleAttribute("aria-current", false)
                    this.updatePageNumber(this.mainCarousel.index);
                }
                const thumbnail = this.thumbnails[this.mainCarousel.index];
                if (thumbnail) {
                    thumbnail.classList.add('is-active');
                    thumbnail.toggleAttribute("aria-current", true)
                    current = thumbnail;
                }
            })
        }

        this.mainCarousel.mount(extensions);
        this.updatePageNumber(0);
    }

    initThumbnail(thumbnail: HTMLElement, index: number) {
        this.thumbnails.forEach(t => t.setAttribute('aria-current', 'false'));

        thumbnail.addEventListener('click', () => {
            this.mainCarousel.go(index);
            thumbnail.setAttribute('aria-current', 'true');
        });
        thumbnail.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key == "Enter")
                this.mainCarousel.go(index);
        });
    }

    initLightbox() {
        const customLightboxHTML = `
        <div id="glightbox-body" class="glightbox-container" tabindex="-1" role="dialog" aria-labelledby="carousel-lightbox-header">
            <div class="gloader visible"></div>
            <div class="goverlay"></div>
            <div class="gcontainer">
            <p class="visually-hidden" id="carousel-lightbox-header" aria-live="polite"></p>
            <div id="glightbox-slider" class="gslider"></div>
            <button class="gclose gbtn" data-taborder="1" aria-label="Close lightbox">{closeSVG}</button>
            <button class="gprev gbtn" data-taborder="2" aria-label="go to previous slide">{prevSVG}</button>
            <button class="gnext gbtn" data-taborder="3" aria-label="go to next slide" >{nextSVG}</button>
            <div class="gthumbnails" data-taborder="4"></div>
            </div>
        </div>`;

        // The selector must be unique for children of a specific carousel, the image carousels uniqueID is added 
        // to the [data-glightbox-selector] prop of each carousel slide to group them all to the lightbox instance.
        this.lightbox = GLightbox({
            selector: `[data-glightbox-selector="${this.parentElem.id}"]`,
            lightboxHTML: customLightboxHTML.toString(),
            loop: true,
            openEffect: 'fade',
            closeEffect: 'fade',
            width: '1920',
            height: '1080',
            draggable: false,
            preload: true,
        });

        this.lightbox.on('open', this.onLightboxOpen.bind(this));
        this.lightbox.on('slide_changed', this.onLightboxSlideChanged.bind(this));
        this.lightbox.on('close', this.onLightboxClosed.bind(this));
    }

    onLightboxOpen(){
        this.lightboxThumbnails = [];
        this.displayThumbnailsInLightbox();
        this.lightboxElem = document.querySelector('.glightbox-container');

        if (this.lightboxElem) {
            const headerElem = this.lightboxElem.querySelector('#carousel-lightbox-header');
            const focusableElements = Array.from(this.lightboxElem?.querySelectorAll<HTMLElement>('a[href]:not([disabled]), button:not([disabled])'));
            this.lightboxElem.addEventListener('keydown', (e) => this.onKeydown.bind(this)(e, focusableElements));
            this.calculateImageContainerHeight();

            if (headerElem) {
                setTimeout(() => {
                    headerElem.textContent = 'Image carousel in fullscreen view';
                }, 100)
            }
        }
    }
    // @ts-ignore
    onLightboxSlideChanged({ current }: BaseData) {
        const actualIndex = current.index % this.carouselModel.itemList.length;
        this.mainCarousel.go(actualIndex);
        this.highlightCurrentThumbnail(actualIndex);
        const liveRegion = this.lightboxElem?.querySelector('.lightbox-live-region') ?? document.createElement('div');
        liveRegion.classList.add('visually-hidden');
        liveRegion.classList.add('lightbox-live-region');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.textContent = `Item ${actualIndex + 1} of ${this.thumbnails.length}`;
        this.lightboxElem?.appendChild(liveRegion);
    }

    onLightboxClosed() {
        const firstCarouselElement = this.mainCarouselElem.querySelectorAll<HTMLAnchorElement>('.splide__slide .glightbox')[0];
        if (firstCarouselElement) {
            setTimeout(() => firstCarouselElement.focus(), 0);
        }
    }

    onKeydown(e: KeyboardEvent, focusableElements: HTMLElement[]) {
        if (e.key === 'Tab') {
            let nextElement;
            let taborder = Number(document.activeElement.getAttribute('data-taborder'));
            if (e.shiftKey) { // Reverse
                e.preventDefault();
                e.stopPropagation();
                focusableElements.forEach(el => el.classList.remove('focused'));
                nextElement = taborder > 1
                    ? focusableElements.find(el => el.getAttribute('data-taborder') == `${taborder - 1}`)
                    : focusableElements.find(el => el.getAttribute('data-taborder') == focusableElements.length.toString());
                nextElement.focus();
                nextElement.classList.add('focused');
            }
        }
    }

    updatePageNumber(index: number) {
        if (index + 1 > this.carouselModel.itemList.length) return;
        const pageNumberWrapper = this.parentElem.querySelector('.splide__page-num');
        pageNumberWrapper.textContent = `${index + 1} of ${this.carouselModel.itemList.length}`;
    }

    renderCarouselHeader(model: CarouselModel) {
        const carouselListWrapper = this.containerElem.querySelector('.splide__list');
        const headerLeft = this.containerElem.querySelector('.carousel__header .left');
        const headerRight = this.containerElem.querySelector('.carousel__header .right');
        headerLeft.innerHTML = `
             <h2 class="h4">
                 ${model.header.Title}
                 <span class="visually-hidden"> project snapshot</span>
             </h2>
            <div class="tag--list">
                ${RenderTagList(model.header.Tags)}
            </div>
        `;
        headerRight.innerHTML = model.header.Content;
        let itemListString = '';
        model.itemList.map(item => {
            itemListString += `<div class="splide__slide"><img src="${item}" /></div>`
        });
        carouselListWrapper.innerHTML = itemListString;
    }

    displayThumbnailsInLightbox() {
        const lightboxContainer = document.querySelector('.gcontainer');
        let currentIndex = this.lightbox.getActiveSlideIndex();
        // Clear any existing thumbnail container
        const existingThumbs = document.querySelector('.gthumbnails');

        if (existingThumbs) existingThumbs.remove();

        // Create thumbnail container
        const thumbnailContainer = document.createElement('div');
        thumbnailContainer.classList.add('gthumbnails');
        thumbnailContainer.style.display = 'flex';
        thumbnailContainer.style.justifyContent = 'center';

        this.thumbnails.forEach((thumb, i) => {
            const image = thumb.querySelector('img');
            const thumbnail = document.createElement('button');
            const thumbImg = document.createElement('img');
            thumbnail.classList.add('thumbnail');
            thumbnail.classList.add('gbtn');
            thumbnail.type = "button";
            thumbnail.setAttribute("data-taborder", `${4 + i}`);
            thumbnail.setAttribute("aria-label", `go to slide ${i + 1}`);
            thumbImg.src = image.src;

            thumbnail.appendChild(thumbImg);
            if (i === currentIndex) {
                thumbnail.classList.add('is-active');  // Highlight the current image
            }
            // Handle click to navigate within GLightbox
            thumbnail.addEventListener('click', (e) => {
                e.stopPropagation();
                this.lightbox.goToSlide(i);
            });
            this.lightboxThumbnails.push(thumbnail);
            thumbnailContainer.appendChild(thumbnail);
        });
        // Append the thumbnail container to the lightbox
        lightboxContainer.appendChild(thumbnailContainer);
    }

    highlightCurrentThumbnail(index: number) {
        this.lightboxThumbnails.forEach((thumb, i) => {
            thumb.classList.remove('is-active');
            thumb.setAttribute('aria-current', 'false')

            if (i === index) {
                thumb.classList.add('is-active');
                thumb.setAttribute('aria-current', 'true')
            }
        });
    }

    updateTabIndexOnCarousel(tabbable: boolean) {
        const mainCarouselItems = this.mainCarouselElem.querySelectorAll('.glightbox') as NodeListOf<HTMLAnchorElement>;

        setTimeout(() => {
            mainCarouselItems.forEach(el => {
                const prevTabindex = el.tabIndex;
                !tabbable ? el.tabIndex = -1 : el.tabIndex = prevTabindex
            });
        }, 50)
    }

    private calculateImageContainerHeight() {
        const descriptionElements: NodeListOf<HTMLElement> = this.lightboxElem.querySelectorAll('.gslide-description');
        const lightboxImages: NodeListOf<HTMLImageElement> = this.lightboxElem.querySelectorAll('.gslide-image');

        descriptionElements.forEach((el: HTMLElement, index) => {
            const height = el.offsetHeight;

            if (lightboxImages[index]) {
                lightboxImages[index].style.height = `calc(100% - ${height}px)`;
            }
        });
    }
}
